import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  CloseRounded as CloseRoundedIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { useMemo } from "react";
import type { ChangeEvent } from "react";
import type { HomeOwnerUser } from "../../../../__generated__/types";
import { VisuallyHiddenInput } from "../../../../components/firebase-image-upload";
import { HIRE_LIKELIHOOD_OPTIONS } from "../../data";
import {
  SECTION_BG,
  SECTION_BORDER,
  SECTION_RADIUS,
  selectSx,
  selectWrapperSx,
} from "../../styles";
import { ReadOnlyChips, ReadOnlyField } from "../shared/Fields";

type BioHeaderSectionProps = {
  displayName: string;
  profilePreview: string | null;
  onProfileUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  onProfileRemove: () => void;
  homeownerUser: Partial<HomeOwnerUser> | null;
  personalityType: string;
  partnershipStyle: string;
  topMatchingAttributes: string[];
  topDesignerSkills: string[];
  hireLikelihood: string;
  onHireLikelihoodChange: (value: string) => void;
};

const BioHeaderSection = ({
  displayName,
  profilePreview,
  onProfileUpload,
  onProfileRemove,
  homeownerUser,
  personalityType,
  partnershipStyle,
  topMatchingAttributes,
  topDesignerSkills,
  hireLikelihood,
  onHireLikelihoodChange,
}: BioHeaderSectionProps) => {
  const selectedHireOption = useMemo(
    () =>
      HIRE_LIKELIHOOD_OPTIONS.find((option) => option.value === hireLikelihood),
    [hireLikelihood]
  );

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={2} sx={{ fontSize: "18px" }}>
        {displayName}'s Bio
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container columnSpacing={2.5} rowSpacing={2} alignItems="flex-start">
            <Grid item xs={12} sm="auto" md="auto">
              <Box
                component="label"
                sx={{
                  border: profilePreview ? "none" : "1px solid #E4E7EF",
                  borderRadius: SECTION_RADIUS,
                  p: profilePreview ? 0 : 2,
                  textAlign: "center",
                  backgroundColor: profilePreview ? "transparent" : "#F2F4F7",
                  width: 205,
                  minWidth: 205,
                  maxWidth: 205,
                  height: 205,
                  minHeight: 205,
                  maxHeight: 205,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  cursor: "pointer",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {profilePreview ? (
                  <Box
                    component="img"
                    src={profilePreview}
                    alt="Profile preview"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: SECTION_RADIUS,
                    }}
                  />
                ) : (
                  <Stack spacing={1} alignItems="center">
                    <AddIcon sx={{ fontSize: 36, color: "#C7CCD6" }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "12px", fontWeight: 600 }}
                    >
                      Add Profile Picture (Optional)
                    </Typography>
                  </Stack>
                )}
                {profilePreview && (
                  <IconButton
                    aria-label="Remove profile image"
                    size="small"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onProfileRemove();
                    }}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      backgroundColor: "#fff",
                      border: "1px solid #E4E7EF",
                      boxShadow: "0px 4px 10px rgba(15, 23, 42, 0.12)",
                      "&:hover": {
                        backgroundColor: "#F8FAFC",
                      },
                    }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                  </IconButton>
                )}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={onProfileUpload}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm md>
              <Box sx={{ maxWidth: { xs: "100%", sm: 444 }, width: "100%" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <ReadOnlyField
                      label="Mailing Address"
                      value={homeownerUser?.address ?? "811 Six Oaks Ln"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ width: { xs: "100%", sm: 282 } }}>
                      <ReadOnlyField label="User Name" value={displayName} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <ReadOnlyField
                      label="Zip Code"
                      value={homeownerUser?.zipCode ?? "38017"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <ReadOnlyField
                      label="City"
                      value={homeownerUser?.city ?? "Los Angeles"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <ReadOnlyField
                      label="State"
                      value={homeownerUser?.state ?? "CA"}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 2,
              backgroundColor: "#F6F9FB",
              borderRadius: SECTION_RADIUS,
              border: "1px solid #EEF1F6",
              p: { xs: 2, md: 2.5 },
            }}
          >
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={2}>
                      <ReadOnlyChips label="Personality Type" values={[personalityType]} />
                      <ReadOnlyChips label="Partnership Style" values={[partnershipStyle]} />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={2}>
                      <ReadOnlyChips
                        label="Top Matching Attributes"
                        values={topMatchingAttributes}
                      />
                      <ReadOnlyChips
                        label="Top 3 Designer Skill"
                        values={topDesignerSkills}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    backgroundColor: "#F1F3F6",
                    borderRadius: SECTION_RADIUS,
                    p: { xs: 2, md: 2 },
                    border: "1px solid #E5E9F2",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    mb={2}
                    sx={{ fontSize: "14px", lineHeight: 1.4 }}
                  >
                    How likely are you to hire a designer in the next 30 days?
                  </Typography>
                  <FormControl fullWidth size="small" sx={selectWrapperSx}>
                    <InputLabel>Project Readiness</InputLabel>
                    <Tooltip
                      title={selectedHireOption?.tooltip ?? ""}
                      arrow
                      placement="top"
                      disableHoverListener={!selectedHireOption}
                    >
                      <Select
                        label="Project Readiness"
                        value={hireLikelihood}
                        onChange={(event) =>
                          onHireLikelihoodChange(event.target.value as string)
                        }
                        sx={selectSx}
                        renderValue={(selected) => (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "13px",
                                lineHeight: 1.3,
                                whiteSpace: "normal",
                              }}
                            >
                              {String(selected)}
                            </Typography>
                            <InfoOutlinedIcon
                              fontSize="small"
                              color="action"
                              sx={{ mt: "2px" }}
                            />
                          </Box>
                        )}
                      >
                        {HIRE_LIKELIHOOD_OPTIONS.map((option) => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                              whiteSpace: "normal",
                              lineHeight: 1.3,
                            }}
                          >
                            {option.value}
                          </MenuItem>
                        ))}
                      </Select>
                    </Tooltip>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BioHeaderSection;
